import { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { Box, Grid, InternalStandardProps } from "@mui/material";
import AddAPhotoOutlinedIcon from "@mui/icons-material/AddAPhotoOutlined";
import icEvent from "../../../Images/Content/ic_event.gif";
import { messaging } from "JS/Helpers/UserMessaging";
import { AppBackdropProgress } from "./Progress/AppBackdropProgress";
import { AppTypography } from "./AppTypography";

const Root = styled(Grid)(({ theme }) => ({
    justifyContent: "center",
    marginTop: theme.spacing(2),
}));

const RootWithPadding = styled(Root)(({ theme }) => ({
    paddingLeft: theme.spacing(2),
}));

const BackgroundImageContainer = styled(Grid)(() => ({
    display: "flex",
    justifyContent: "center",
    marginTop: "2%",
    marginBottom: "3%",
}));

const BackImageStyle = styled("img")(({ theme }) => ({
    maxWidth: "300px",
    maxHeight: "350px",
    objectFit: "contain",
    [theme.breakpoints.up("md")]: {
        maxWidth: "450px",
        maxHeight: "450px",
    },
}));

const ImageBoxContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    background: theme.palette.grey["100"],
    padding: theme.spacing(2),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
}));

const AddPhotoPlaceholder = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    cursor: "pointer",
    padding: theme.spacing(2),
    width: "300px",
    height: "250px",
    maxWidth: "100%",
    color: theme.palette.grey[600],
    border: `2px dashed ${theme.palette.grey[500]}`,
    borderRadius: theme.spacing(1),
    [theme.breakpoints.up("md")]: {
        width: "450px",
        height: "350px",
    },
}));

const LtdGif = styled("img")(({ theme }) => ({
    background: "white",
    position: "absolute",
    width: "80px",
    height: "80px",
    border: `4px solid ${theme.palette.grey[500]}`,
    borderRadius: "20%",
    right: "-10px",
    marginRight: "10px",
    top: "-35px",
}));

export interface userImageProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {
    isImageExists: boolean;
    backgroundImageUrl: string;
    handleUploadClick: () => void;
    isDefaultBehaviour: boolean;
    setDialogs: React.Dispatch<
        React.SetStateAction<{
            uploadImageDialog: boolean;
            webCamImageDialog: boolean;
        }>
    >;
    dialogs: {
        uploadImageDialog: boolean;
        webCamImageDialog: boolean;
    };
    handleUploadImage: (event: any) => void;
    hdnFileInput: any;
    fetchingImage: boolean;
    isFromFYC?: boolean;
    imageFetchFailed?: boolean;
    imageCheckPending?: boolean;
}

export const UserImage = (props: userImageProps) => {
    const {
        isImageExists,
        backgroundImageUrl,
        handleUploadClick,
        isDefaultBehaviour,
        setDialogs,
        dialogs,
        handleUploadImage,
        hdnFileInput,
        fetchingImage,
        isFromFYC,
        imageFetchFailed,
        imageCheckPending,
    } = props;
    const [imageLoading, setImageLoading] = useState(false);
    const [imageLoadFailed, setImageLoadFailed] = useState(false);

    const hasImage = isImageExists && !!backgroundImageUrl && !imageLoadFailed;

    useEffect(() => {
        setImageLoadFailed(false);
    }, [backgroundImageUrl]);

    useEffect(() => {
        if (fetchingImage) {
            setImageLoading(true);
        } else if (!hasImage) {
            // no <img> will mount, so nothing else can clear the spinner
            setImageLoading(false);
        }
    }, [fetchingImage, hasImage]);

    useEffect(() => {
        if (hasImage) {
            setImageLoading(true);
        }
    }, [hasImage, backgroundImageUrl]);

    const showSpinner = fetchingImage || imageLoading;
    // never claim "no photo" before the check has resolved one way or the other
    const showPlaceholder = !hasImage && !showSpinner && !imageCheckPending;

    const handleImageClick = isDefaultBehaviour
        ? () => setDialogs({ ...dialogs, uploadImageDialog: true })
        : () => handleUploadClick();

    const getUploadPhotoText = () => {
        // the request failed, so whether a photo exists is unknown
        if (imageFetchFailed) {
            return messaging?.events?.photoFetchFailed;
        }
        // the photo was uploaded before, so the "upload to view" prompt no longer applies
        if (imageLoadFailed) {
            return messaging?.events?.reuploadPhoto;
        }
        return isFromFYC
            ? messaging?.fycStatus?.uploadPhoto
            : messaging?.events?.uploadPhotoToViewTicket;
    };

    const RootComponent = isFromFYC ? RootWithPadding : Root;

    return (
        <>
            <RootComponent container>
                <BackgroundImageContainer item xs={12}>
                    {showSpinner && (
                        <AppBackdropProgress
                            open={showSpinner}
                            backdropText={messaging?.events?.fetchingPhoto}
                        />
                    )}
                    {hasImage && (
                        <ImageBoxContainer>
                            <LtdGif src={icEvent} alt="Not-Found" />
                            <BackImageStyle
                                src={backgroundImageUrl}
                                alt="Not-Found"
                                data-testid="userPhoto"
                                onLoad={() => setImageLoading(false)}
                                onError={() => {
                                    // signed url expired or object removed from s3
                                    setImageLoading(false);
                                    setImageLoadFailed(true);
                                }}
                                onClick={handleImageClick}
                            />
                        </ImageBoxContainer>
                    )}
                    {showPlaceholder && (
                        <ImageBoxContainer>
                            <LtdGif src={icEvent} alt="Not-Found" />
                            <AddPhotoPlaceholder
                                data-testid="addPhotoPlaceholder"
                                onClick={handleImageClick}
                            >
                                <AddAPhotoOutlinedIcon fontSize="large" />
                                <AppTypography
                                    variant="body1"
                                    textAlign="center"
                                >
                                    {getUploadPhotoText()}
                                </AppTypography>
                            </AddPhotoPlaceholder>
                        </ImageBoxContainer>
                    )}
                </BackgroundImageContainer>
            </RootComponent>
            {!isDefaultBehaviour && (
                <input
                    style={{ display: "none" }}
                    type="file"
                    ref={hdnFileInput}
                    data-testid="imageUpload"
                    accept="image/png, image/gif, image/jpeg"
                    id="imageUpload"
                    onChange={(event) => handleUploadImage(event)}
                />
            )}
        </>
    );
};
